/**
 * P2.3b-2 OPS Console V2: KI-Preisverwaltung.
 *
 * Abschnitt „Einstellungen → KI-Preise".
 *  - Stufe 1: read-only Tabelle der versionierten `llm_price_table`.
 *  - Stufe 2a: Formular „Neue Preisversion anlegen" (Write, additiv). Es wird NUR
 *    eine neue Version angelegt – KEIN Editieren/Löschen/Deaktivieren historischer
 *    Preise. Optional wird die vorherige offene Version desselben Modells
 *    automatisch geschlossen (Checkbox, Default AN). Währung fix EUR.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Coins, Loader2, Plus, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { priceApi } from "@/lib/priceApi";
import { opsErrorMessage } from "@/lib/opsApi";

function fmtNum(n) {
  if (n == null) return "–";
  try {
    return Number(n).toLocaleString("de-DE");
  } catch {
    return String(n);
  }
}

function fmtDateTime(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

const EMPTY_FORM = {
  provider: "",
  model: "",
  inputMicro: "0",
  outputMicro: "0",
  validFrom: "",
  source: "",
  closePrevious: true,
};

export function PriceEditorSection() {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    priceApi
      .list()
      .then((d) => setPrices(d.prices || []))
      .catch((e) => setLoadErr(opsErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (busy) return;
    if (!form.provider.trim() || !form.model.trim() || !form.validFrom) {
      toast.error("Bitte Provider, Modell und „Gültig ab“ ausfüllen.");
      return;
    }
    setBusy(true);
    try {
      const res = await priceApi.create({
        provider: form.provider.trim(),
        model: form.model.trim(),
        input_micro_eur_per_1k: Number(form.inputMicro),
        output_micro_eur_per_1k: Number(form.outputMicro),
        valid_from: form.validFrom,
        source: form.source.trim(),
        close_previous: form.closePrevious,
      });
      const closed = res?.closed_previous || 0;
      toast.success(
        closed > 0
          ? `Neue Preisversion angelegt · ${closed} vorherige Version geschlossen.`
          : "Neue Preisversion angelegt.",
      );
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(opsErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="ops-prices-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Coins className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Einstellungen → KI-Preise
            </h2>
            <p className="text-xs text-slate-500">
              Werte in µ€ pro 1.000 Tokens (1 € = 1.000.000 µ€). Neue Versionen anlegen –
              historische Preise werden nicht verändert oder gelöscht.
            </p>
          </div>
        </div>
        <Button
          data-testid="ops-prices-refresh"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Stufe 2a – Neue Preisversion anlegen */}
      <form onSubmit={handleCreate} className="px-5 py-4 border-b border-slate-100 bg-slate-50/60" data-testid="ops-price-create-form">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
          Neue Preisversion anlegen
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="price-provider" className="text-xs">Provider</Label>
            <Input
              id="price-provider"
              data-testid="ops-price-input-provider"
              value={form.provider}
              onChange={(e) => setField("provider", e.target.value)}
              placeholder="z. B. Emergent"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="price-model" className="text-xs">Modell</Label>
            <Input
              id="price-model"
              data-testid="ops-price-input-model"
              value={form.model}
              onChange={(e) => setField("model", e.target.value)}
              placeholder="z. B. claude-sonnet-4-6"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="price-input-micro" className="text-xs">Input (µ€/1.000)</Label>
            <Input
              id="price-input-micro"
              data-testid="ops-price-input-input-micro"
              type="number"
              min="0"
              step="1"
              value={form.inputMicro}
              onChange={(e) => setField("inputMicro", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="price-output-micro" className="text-xs">Output (µ€/1.000)</Label>
            <Input
              id="price-output-micro"
              data-testid="ops-price-input-output-micro"
              type="number"
              min="0"
              step="1"
              value={form.outputMicro}
              onChange={(e) => setField("outputMicro", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="price-valid-from" className="text-xs">Gültig ab</Label>
            <Input
              id="price-valid-from"
              data-testid="ops-price-input-valid-from"
              type="datetime-local"
              value={form.validFrom}
              onChange={(e) => setField("validFrom", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="price-currency" className="text-xs">Währung</Label>
            <Input id="price-currency" data-testid="ops-price-currency" value="EUR" disabled readOnly />
          </div>
          <div className="flex flex-col gap-1 lg:col-span-2">
            <Label htmlFor="price-source" className="text-xs">Quelle / Bemerkung</Label>
            <Input
              id="price-source"
              data-testid="ops-price-input-source"
              value={form.source}
              onChange={(e) => setField("source", e.target.value)}
              placeholder="z. B. Preisliste 07/2026"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              data-testid="ops-price-close-previous"
              checked={form.closePrevious}
              onChange={(e) => setField("closePrevious", e.target.checked)}
              className="w-4 h-4 accent-slate-900"
            />
            Vorherige offene Version dieses Modells schließen (valid_until = „Gültig ab“)
          </label>
          <Button
            type="submit"
            data-testid="ops-price-create-submit"
            disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Preisversion speichern
          </Button>
        </div>
      </form>

      {loading && (
        <div className="px-5 py-10 flex items-center justify-center text-slate-400" data-testid="ops-prices-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && loadErr && (
        <div className="px-5 py-6" data-testid="ops-prices-error">
          <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {loadErr}
            </p>
            <Button variant="outline" size="sm" onClick={load}>
              Erneut versuchen
            </Button>
          </div>
        </div>
      )}

      {!loading && !loadErr && (
        <div className="overflow-x-auto">
          <Table data-testid="ops-prices-table">
            <TableHeader>
              <TableRow>
                <TableHead>Provider</TableHead>
                <TableHead>Modell</TableHead>
                <TableHead>Währung</TableHead>
                <TableHead className="text-right">Input (µ€/1.000)</TableHead>
                <TableHead className="text-right">Output (µ€/1.000)</TableHead>
                <TableHead>Gültig ab</TableHead>
                <TableHead>Gültig bis</TableHead>
                <TableHead>Aktiv</TableHead>
                <TableHead>Quelle</TableHead>
                <TableHead>Erstellt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-slate-400 py-8" data-testid="ops-prices-empty">
                    Keine Preisdatensätze vorhanden.
                  </TableCell>
                </TableRow>
              )}
              {prices.map((p, idx) => (
                <TableRow key={`${p.model}-${p.valid_from}-${idx}`} data-testid={`ops-price-row-${idx}`}>
                  <TableCell className="text-sm text-slate-700">{p.provider || "–"}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-900">{p.model || "–"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.currency || "–"}</TableCell>
                  <TableCell className="text-right text-sm text-slate-700">{fmtNum(p.input_micro_eur_per_1k)}</TableCell>
                  <TableCell className="text-right text-sm text-slate-700">{fmtNum(p.output_micro_eur_per_1k)}</TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">{fmtDateTime(p.valid_from)}</TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                    {p.valid_until ? fmtDateTime(p.valid_until) : "offen"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {p.active ? (
                      <span className="text-emerald-600 font-medium">ja</span>
                    ) : (
                      <span className="text-slate-400">nein</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[240px] truncate" title={p.source || ""}>
                    {p.source || "–"}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">{fmtDateTime(p.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
